import defaultImportAnalytics from '@cointracker/landing-app/src/common/analytics';
import { Grid } from '@cointracker/styleguide/src/Grid';
import { Button } from '@cointracker/styleguide/src/LoggedIn/Button';
import * as Modal from '@cointracker/styleguide/src/Modal';
import { Body2 } from '@cointracker/styleguide/src/typography';
import classNames from 'classnames';
import { forwardRef, useState, type Ref } from 'react';
import { FinalBlock } from './FinalBlock/FinalBlock';
import {
  QuestionBlock,
  type QuizFlowQuestion,
} from './QuestionBlock/QuestionBlock';
import {
  button,
  buttons,
  inner,
  mobileButtons,
  modalBody,
  root,
} from './QuizFlow.css';
import { QUIZ_FLOW_QUESTIONS } from './quiz-flow-questions';

export interface QuizFlowProps {
  className?: string;
  questions?: QuizFlowQuestion[];
  analytics?: typeof defaultImportAnalytics;
}

export const QuizFlow = forwardRef(
  (
    {
      className,
      questions = QUIZ_FLOW_QUESTIONS,
      analytics,
      ...rest
    }: QuizFlowProps,
    ref: Ref<HTMLDivElement>,
  ) => {
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

    const firstQuestion = questions[0];
    const currentQuestion = questions[currentQuestionIndex];
    const analyticsToUse = analytics ?? defaultImportAnalytics;
    const questionTitle = currentQuestion
      ? `Question ${currentQuestionIndex + 1} / ${questions.length}`
      : undefined;

    const onClickBack =
      currentQuestionIndex > 0
        ? () => {
            setCurrentQuestionIndex(currentQuestionIndex - 1);
          }
        : undefined;

    const onAnswer = (answerIndex: number | null) => {
      if (answerIndex !== null) {
        analyticsToUse.track('Post Quiz Question Chosen', {
          question: questions[currentQuestionIndex]?.text,
          choice: questions[currentQuestionIndex]?.answers[answerIndex],
        });
      }
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    };

    return (
      <div
        className={classNames(
          'bg-background-alternate-lighter',
          root,
          className,
        )}
        ref={ref}
        {...rest}
      >
        <Grid className={inner}>
          <Modal.Root>
            <Body2 weight="bold">{firstQuestion?.text}</Body2>
            <div className={buttons}>
              {firstQuestion?.answers.map((answer, i) => (
                <Modal.Trigger
                  asChild
                  key={i}
                  onClick={() => setCurrentQuestionIndex(1)}
                >
                  <Button variant="line" className={button}>
                    {answer}
                  </Button>
                </Modal.Trigger>
              ))}
            </div>
            <div className={mobileButtons}>
              <Modal.Trigger asChild>
                <Button
                  variant="line"
                  className={button}
                  onClick={() => setCurrentQuestionIndex(0)}
                >
                  Select an answer
                </Button>
              </Modal.Trigger>
            </div>
            <Modal.Content fullscreen>
              <Modal.ContentHeader
                onClickBack={onClickBack}
                title={questionTitle}
              />
              <Modal.ContentBody className={modalBody}>
                {currentQuestion ? (
                  <QuestionBlock
                    question={currentQuestion}
                    onAnswer={onAnswer}
                  />
                ) : (
                  <FinalBlock />
                )}
              </Modal.ContentBody>
            </Modal.Content>
          </Modal.Root>
        </Grid>
      </div>
    );
  },
);

QuizFlow.displayName = 'QuizFlow';
